import type {ThemeAndAnyColor, WithThemeProp} from '../theme';

import {getColor} from './getColor';

type ColorSchemeConfig<
  P extends WithColorSchemeProp,
  T extends string,
> = Record<P['scheme'], ColorSchemeItem<T>>;

type ColorSchemeItem<T extends string> = Record<T, ThemeAndAnyColor>;

type ColorSchemeKeyDefault =
  | 'active'
  | 'disabled'
  | 'hover'
  | 'primary'
  | 'secondary';

interface WithColorSchemeProp {
  scheme: string;
}

/**
 * Utility to create components color scheme.
 *
 * Always pass first generic argument(component's Props interface), its REQUIRED!!!.
 *
 * If your scheme have different than ColorSchemeKeyDefault keys,
 * pass second generic argument with your scheme keys,
 * for example: const scheme = makeColorScheme<ComponentProps, 'customKey1' | 'customKey2' | 'cKey3'.
 *
 * To get color from the theme use string token ('button.fill').
 * @param config
 */
export function makeColorScheme<
  ComponentPropsType = void,
  SchemeKey extends string = ColorSchemeKeyDefault,
>(
  config: ColorSchemeConfig<
    ComponentPropsType & WithColorSchemeProp,
    SchemeKey
  > &
    ComponentPropsType extends void
    ? 'ERROR: You did NOT provide ComponentPropsType'
    : ComponentPropsType extends WithColorSchemeProp
      ? ColorSchemeConfig<ComponentPropsType, SchemeKey>
      : 'ERROR: ComponentPropsType does NOT implement WithColorSchemeProps interface',
) {
  const scheme = Object.assign({}, config) as unknown as ColorSchemeConfig<
    ComponentPropsType & WithColorSchemeProp,
    SchemeKey
  >;

  return {
    ...scheme,
    getColor:
      (key: SchemeKey) =>
      (props: ComponentPropsType & WithThemeProp & WithColorSchemeProp) =>
        getColor(scheme[props.scheme][key])(props),
  };
}
